import {
    CoachingEventStatus,
    CoachingType,
    CommitmentSummaryListItem,
    Opportunity,
} from '../../coaching-base/general';
import {
    opportunityToViewTransformer,
    OpportunityView,
    sortOpportunities,
} from '../../coaching-base/opportunity';

import { CommitmentGoalLookup } from './commitment-goal-lookup.model';
import { CommitmentView } from './commitment-view.model';
import { NonKpi } from './non-kpi.model';
import { OpportunityCategory } from './opportunity-category.model';
import { TranslationKeyMap } from './pillar-one.i18n';

interface GroupedOpportunities {
    coached: OpportunityView[];
    unCoached: OpportunityView[];
    missed: OpportunityView[];
    nonOpportunities: OpportunityView[];
    nonKpis: CommitmentView[];
}

const coachingEventStatusTranslationMap = {
    [CoachingEventStatus.DRAFT]: TranslationKeyMap._9476_DRAFT,
    [CoachingEventStatus.PRE_APPROVAL]: TranslationKeyMap._9495_PRE_APPROVAL,
    [CoachingEventStatus.ACTIVE]: TranslationKeyMap._125_ACTIVE,
    [CoachingEventStatus.UPDATING]: TranslationKeyMap._8702_UPDATING,
    [CoachingEventStatus.PENDING_CLOSE]: TranslationKeyMap._7441_PENDING_CLOSE,
    [CoachingEventStatus.CLOSED]: TranslationKeyMap._7440_CLOSED,
};

export function distillOpportunities(
    opportunities: Opportunity[],
    nonKpis: NonKpi[],
): GroupedOpportunities {
    const oppItems = opportunities || [];
    const nonKpiItems = nonKpis || [];

    const oppItemsFinal = opportunityToViewTransformer(oppItems, oppItems).sort(
        (a, b) => sortOpportunities(a, b, 'asc'),
    );

    // TODO post-refactor oa.model:defaultMetricSort
    const coached = oppItemsFinal.filter(
        (e) => e.coachingType === CoachingType.COACHED,
    );

    // TODO post-refactor oa.model:defaultMetricSort
    const unCoached = oppItemsFinal.filter(
        (e) => e.coachingType === CoachingType.UNCOACHED,
    );

    const missed = oppItemsFinal.filter(
        (e) => e.coachingType === CoachingType.MISSED,
    );

    const nonOpportunities = oppItemsFinal.filter(
        (e) => e.coachingType === CoachingType.NON_OA,
    );

    const nextNonKpis = [...nonKpiItems]
        .map<CommitmentView>((e) => ({
            commitmentId: e.commitmentId,
            statusId: e.statusId,
            status: coachingEventStatusTranslationMap[e.statusId],
            behaviour: e.behaviourName,
            subBehaviour: e.subBehaviourName,
        }))
        .sort((a, b) => b.commitmentId - a.commitmentId);

    return {
        coached,
        unCoached,
        missed,
        nonOpportunities,
        nonKpis: nextNonKpis,
    };
}

export function categorizeOpportunities(
    opportunities: GroupedOpportunities, // all props inside opportunities are expected to be defined
    activeNonOpportunityCommitments: boolean,
    activeMetricId: number,
    isNonKpi?: boolean,
): {
    categories: OpportunityCategory[];
    nonOpportunitiesOpen: boolean;
    nonKpisOpen: boolean;
} {
    const categories = [
        {
            label: TranslationKeyMap._7488_COACHED,
            isOpen:
                opportunities.coached.length !== 0 ||
                opportunities.coached.some(
                    (e) => e.metricId === activeMetricId,
                ),
            items: opportunities.coached,
        },
        {
            label: TranslationKeyMap._7489_UNCOACHED,
            isOpen:
                opportunities.unCoached.length !== 0 ||
                opportunities.unCoached.some(
                    (e) => e.metricId === activeMetricId,
                ),
            items: opportunities.unCoached,
        },
        {
            label: TranslationKeyMap._7508_MISSED_OPPORTUNITIES,
            isOpen: false,
            items: opportunities.missed,
        },
    ];

    const nonOpportunitiesOpen =
        activeNonOpportunityCommitments ||
        opportunities.nonOpportunities.some(
            (e) => e.metricId === activeMetricId,
        );

    const nonKpisOpen =
        opportunities.nonKpis.filter(
            (e) => e.statusId === CoachingEventStatus.ACTIVE,
        ).length !== 0 || isNonKpi;

    return {
        categories,
        nonOpportunitiesOpen,
        nonKpisOpen,
    };
}

export function createCommitmentGoalLookup(
    commitments: CommitmentSummaryListItem[],
): CommitmentGoalLookup {
    const cmtItems = commitments || [];
    const lookup: { [metricId: number]: number } = {};
    const metricsIds = [];

    cmtItems.forEach((c) => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const identifier = (c.goals || []).map((e) => [
            e.metricId,
            `${e.metricId}--${
                c.commitmentId != null
                    ? c.commitmentId
                    : // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                      c.draftAndApproval && c.draftAndApproval.draftId
            }`,
        ]);
        const uniqueIds = Array.from(new Set(identifier));
        uniqueIds.forEach((e) => {
            if (metricsIds.includes(e[1])) {
                return;
            }
            metricsIds.push(e[1]);
            if (lookup[e[0]] == null) {
                lookup[e[0]] = 0;
            }
            lookup[e[0]]++;
        });
    });
    return lookup;
}

export function checkMetricIdInMultiple(
    metricId: number,
    commitmentGoalLookup: CommitmentGoalLookup,
): boolean {
    return (
        metricId != null &&
        commitmentGoalLookup != null &&
        commitmentGoalLookup[metricId] != null &&
        commitmentGoalLookup[metricId] >= 2
    );
}

export function findOpportunity(
    opportunities: OpportunityCategory[],
    nonOpportunities: OpportunityView[],
    metricId: number,
): OpportunityView {
    const opps: OpportunityView[] = [];
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    opportunities &&
        opportunities.forEach((e) => {
            opps.push(...e.items);
        });
    nonOpportunities && opps.push(...nonOpportunities);
    return opps.find((e) => e.metricId === metricId);
}

/** ensure category containing active/selected metric is open */
export function ensureSelectedIsOpen(
    oppCategories: OpportunityCategory[],
    activeMetricId: number,
): OpportunityCategory[] {
    let nextOppCategories = oppCategories;
    if (nextOppCategories) {
        const catIndex = nextOppCategories.findIndex((cat) => {
            return cat.items.some((e) => e.metricId === activeMetricId);
        });
        if (catIndex !== -1) {
            nextOppCategories = [...nextOppCategories];
            nextOppCategories[catIndex] = {
                ...nextOppCategories[catIndex],
                isOpen: true,
            };
        }
    }
    return nextOppCategories;
}
